import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { SerializedError } from '@reduxjs/toolkit';

import { Spinner } from '@/components/ui/spinner';
import { Alert, AlertDescription } from '@/components/ui/alert';

import { ConsignmentLineDetailsFormModal } from '@/features/consignment-line-details-modal';
import type { ConsignmentLineItem } from '@/entities/consignments';

import { useProjectSiteParams } from '@/shared/lib';
import { DataTable } from '@/shared/ui/data-table';

import { useConsignmentLinesTable } from '../model/use-consignment-lines-table';
import type { ConsignmentLinesData } from '../model/types';

interface ConsignmentLinesTableProps {
  consignmentId: string;
  consignmentStatus?: string;
  isReadOnly?: boolean;
  /** Optional external data to use instead of fetching from API */
  data?: ConsignmentLinesData;
  /** Optional loading state for external data */
  isLoading?: boolean;
  /** Optional error state for external data */
  error?: FetchBaseQueryError | SerializedError;
  /** Table customization */
  tableId?: string;
  selectable?: boolean;
  selectedIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
  showToolbar?: boolean;
  /** Replaces the inventory units cell, e.g. with an editable quantity */
  renderInventoryUnits?: (row: ConsignmentLineItem) => ReactNode;
}

export function ConsignmentLinesTable({
  consignmentId,
  consignmentStatus,
  isReadOnly = false,
  data: externalData,
  isLoading: externalIsLoading,
  error: externalError,
  tableId = 'consignment-lines-table',
  selectable = false,
  selectedIds,
  onSelectionChange,
  showToolbar = false,
  renderInventoryUnits
}: ConsignmentLinesTableProps) {
  const { t } = useTranslation();
  const { projectId, siteId } = useProjectSiteParams();

  const {
    selectedLineId,
    lineModalMode,
    isLoading,
    error,
    tableData,
    columns,
    actions,
    handleCloseModal,
    canDeleteLine
  } = useConsignmentLinesTable({
    consignmentId,
    projectId,
    siteId,
    consignmentStatus,
    isReadOnly,
    externalData,
    externalIsLoading,
    externalError,
    renderInventoryUnits
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{t('consignment.lines.error', 'Failed to load consignment lines')}</AlertDescription>
      </Alert>
    );
  }

  if (!tableData || tableData.length === 0) {
    return (
      <Alert>
        <AlertDescription>{t('consignment.lines.noData', 'No consignment lines available')}</AlertDescription>
      </Alert>
    );
  }

  return (
    <>
      <DataTable
        data={tableData}
        columns={columns}
        title=""
        tableId={tableId}
        showToolbar={showToolbar}
        enableFullscreen={false}
        enableColumnCustomization={false}
        enableExport={false}
        actions={actions}
        selectable={selectable}
        selectedIds={selectedIds}
        onSelectionChange={onSelectionChange}
        getRowId={(row) => row.id}
        cardsOnMobile
      />

      <ConsignmentLineDetailsFormModal
        lineId={selectedLineId}
        consignmentId={consignmentId}
        isOpen={!!selectedLineId}
        onClose={handleCloseModal}
        onDeleteSuccess={handleCloseModal}
        mode={lineModalMode}
        canDeleteLine={canDeleteLine}
      />
    </>
  );
}
